import { useState } from 'react';
import type { Environment, Project } from '@hangar/shared';
import { stagger } from '@/lib/motion';
import { useCanConfigure, useFeature } from '@/lib/permissions';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { EmptyState } from '@/components/EmptyState';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { Tag } from '@/components/badges';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArchiveEnvironmentDialog } from './ArchiveEnvironmentDialog';
import { ArchivedEnvironments } from './ArchivedEnvironments';
import { EnvironmentForm } from './EnvironmentForm';
import { EnvironmentsPhoneList, type EnvironmentListProps } from './EnvironmentsPhoneList';
import { EnvironmentsTable } from './EnvironmentsTable';
import { archivedEnvironments, liveEnvironments } from './environmentHelpers';
import { addToggle, editBlockReason, environmentActions, type EditingTarget } from './environmentsCardModel';

const EDIT_UNAVAILABLE = 'Editing environments is not available on this Hangar yet';

/** Permissions and the two pieces of state of the card: which row is being edited and which one is being archived. */
function useEnvironmentsEditor(project: Project) {
  const can = useCanConfigure(project.id);
  const editable = useFeature('environmentsEdit');
  const blockedReason = editable === false ? EDIT_UNAVAILABLE : undefined;
  const [editing, setEditing] = useState<EditingTarget>(null);
  const [archiving, setArchiving] = useState<Environment | null>(null);
  const reason = editBlockReason(can, blockedReason);
  const actionsFor = (env: Environment) => environmentActions({ hidden: can.hidden, reason, onEdit: () => setEditing(env.id), onArchive: () => setArchiving(env) });
  return { can, blockedReason, editing, setEditing, archiving, setArchiving, actionsFor };
}

/**
 * Design 28 · Environments: one row per live environment (pill, protection, allowed branches as mono chips, forks and
 * untrusted refs) with Edit and Archive; the editor opens inline under its row, "Add environment" opens it under the
 * table. Archived environments keep their slug and can be restored. Writes need admin or the project's maintainer
 * role and the API's `environmentsEdit` capability. Builds (30d) has no endpoint and is not shown.
 */
export function EnvironmentsCard({ project, id, index }: { project: Project; id: string; index: number }) {
  const ed = useEnvironmentsEditor(project);
  const live = liveEnvironments(project);
  const archived = archivedEnvironments(project);
  return (
    <Card id={id} data-testid="settings-environments" className="enter scroll-mt-24" style={stagger(index)}>
      <EnvironmentsHeader count={live.length} ed={ed} />
      <CardContent className="p-0 sm:p-0">
        <EnvironmentsBody list={{ project, live, editing: ed.editing, setEditing: ed.setEditing, actionsFor: ed.actionsFor }} />
        {ed.editing === 'new' ? (
          <div className="border-t">
            <EnvironmentForm key="new" project={project} environment={undefined} onDone={() => ed.setEditing(null)} />
          </div>
        ) : null}
        {archived.length ? <ArchivedEnvironments project={project} archived={archived} canRestore={ed.can.allowed && !ed.blockedReason} hidden={ed.can.hidden} /> : null}
      </CardContent>
      <ArchiveEnvironmentDialog project={project} env={ed.archiving} onOpenChange={(o) => (o ? undefined : ed.setArchiving(null))} />
    </Card>
  );
}

function EnvironmentsHeader({ count, ed }: { count: number; ed: ReturnType<typeof useEnvironmentsEditor> }) {
  const toggle = addToggle(ed.editing);
  return (
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        Environments <Tag className="tabular">{count}</Tag>
      </CardTitle>
      <CardDescription>Release stages in promotion order, the branches each one accepts and whether forks may build</CardDescription>
      {ed.can.hidden ? null : (
        <CardAction>
          <GatedButton variant="outline" data-testid="env-add" can={ed.can} blockedReason={ed.blockedReason} aria-expanded={toggle.expanded} onClick={() => ed.setEditing(toggle.next)}>
            <Icon name={toggle.icon} size={18} />
            {toggle.label}
          </GatedButton>
        </CardAction>
      )}
    </CardHeader>
  );
}

function EnvironmentsBody({ list }: { list: EnvironmentListProps }) {
  const phone = useBreakpoint() === 'phone';
  if (list.live.length === 0 && list.editing !== 'new') return <NoEnvironments />;
  return phone ? <EnvironmentsPhoneList {...list} /> : <EnvironmentsTable {...list} />;
}

function NoEnvironments() {
  return <EmptyState compact icon="account_tree" title="No environments yet" description="Add staging, preprod and production: builds always target one, and protected ones only build the branches you list." />;
}
